import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  MenuItem,
} from '@mui/material';
import { AU_STATES, US_STATES } from './constants';
import { staticFileLocation } from '@server/constants';
import { WotcSurveyData } from './types';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useUserCountry } from '@context/UserCountryContext';

const defaultValues = {
  snap1: 'Y',
  armed1f: 'Y',
  snap1_recipient_first_name: '',
  snap1_recipient_last_name: '',
  snap1_recipient_city: '',
  snap1_recipient_state: 'AK',
  snap2: 'N',
  snap2_recipient_first_name: '',
  snap2_recipient_last_name: '',
  snap2_recipient_city: '',
  snap2_recipient_state: 'AK',
};

type SnapRecipientFormModalProps = {
  snapRecipientFormModal: boolean;
  wotcSurveyData: WotcSurveyData;
  handleClose: () => void;
};

const SnapRecipientFormModal: FC<SnapRecipientFormModalProps> = ({
  snapRecipientFormModal,
  wotcSurveyData,
  handleClose,
}) => {
  const { userCountry } = useUserCountry();
  const states = userCountry === 'au' ? AU_STATES : US_STATES;
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });

  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues,
  });

  const snap1 = watch('snap1');
  const snap2 = watch('snap2');
  const snap1FirstName = watch('snap1_recipient_first_name');
  const snap1City = watch('snap1_recipient_city');
  const snap1State = watch('snap1_recipient_state');
  const snap1LastName = watch('snap1_recipient_last_name');
  const armed1f = watch('armed1f');

  // Function to handle form submission
  const onSubmit = (data: {
    snap1: any;
    armed1f: string;
    snap1_recipient_first_name: any;
    snap1_recipient_last_name: any;
    snap1_recipient_city: any;
    snap1_recipient_state: any;
    snap2: any;
    snap2_recipient_first_name: any;
    snap2_recipient_last_name: any;
    snap2_recipient_city: any;
    snap2_recipient_state: any;
  }) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };
    const transformedData = {
      // ...wotcSurveyData,
      ...data,
      status: 2,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };
  useEffect(() => {
    if (snap1 === 'Y' && !(snap2 === 'Y')) {
      setValue('snap1_recipient_first_name', snap1FirstName);
      setValue('snap2_recipient_first_name', snap1FirstName);
      setValue('snap1_recipient_last_name', snap1LastName);
      setValue('snap2_recipient_last_name', snap1LastName);
      setValue('snap1_recipient_city', snap1City);
      setValue('snap2_recipient_city', snap1City);
      setValue('snap1_recipient_state', snap1State);
      setValue('snap2_recipient_state', snap1State);
      setValue('armed1f', armed1f);
    }
  }, [
    snap1,
    snap2,
    snap1FirstName,
    snap1City,
    snap1State,
    snap1LastName,
    setValue,
  ]);

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={snapRecipientFormModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="5px"
        >
          <img
            src={`${staticFileLocation}img/images/icon3.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">
            Supplemental Nutrition Assistance Program
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          {/* Main Question */}
          <FormControl component="fieldset" fullWidth>
            <Box
              mb={2}
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: '5px',
              }}
            >
              <FormLabel component="legend">
                Are you a member of a family that receives supplemental
                Nutrition Assistance program (SNAP) benefits (food stamps) for
                the 6 months before you were hired?
              </FormLabel>
              <Controller
                name="snap1"
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                    <FormControlLabel
                      value="U"
                      control={<Radio />}
                      label="Unsure"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>

          {/* Second Question */}
          {snap1 === 'Y' && (
            <FormControl component="fieldset" fullWidth sx={{ mt: 1 }}>
              <Box
                mb={3}
                sx={{
                  display: 'flex',
                  alignItems: {
                    xs: 'start',
                    sm: 'center',
                  },
                  justifyContent: 'space-between',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                }}
              >
                <FormLabel component="legend">
                  Or, received SNAP benefits for at least a 3 month period
                  within the last 5 months but you are no longer receiving them?
                </FormLabel>
                <Controller
                  name="snap2"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                      <FormControlLabel
                        value="Y"
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="N"
                        control={<Radio />}
                        label="No"
                      />
                      <FormControlLabel
                        value="U"
                        control={<Radio />}
                        label="Unsure"
                      />
                    </RadioGroup>
                  )}
                />
              </Box>
            </FormControl>
          )}

          {/* Conditional Fields for Second Question */}
          {snap1 === 'Y' && snap2 === 'Y' && (
            <>
              <Stack
                spacing={2}
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                mb={3}
              >
                {/* First Name */}
                <Controller
                  name="snap2_recipient_first_name"
                  control={control}
                  rules={{ required: 'First name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Enter the first name of the primary recipient*"
                      fullWidth
                      size="small"
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />

                {/* Last Name */}
                <Controller
                  name="snap2_recipient_last_name"
                  control={control}
                  rules={{ required: 'Last name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Enter the last name of the recipient*"
                      fullWidth
                      size="small"
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Stack>
              <Stack
                spacing={2}
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                mb={0}
              >
                {/* City */}
                <Controller
                  name="snap2_recipient_city"
                  control={control}
                  rules={{ required: 'City is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Enter the city where the benefits were received*"
                      fullWidth
                      size="small"
                      margin="normal"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />

                {/* State */}
                <Controller
                  name="snap2_recipient_state"
                  control={control}
                  rules={{ required: 'State is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      size="small"
                      label="Enter the state where benefits were received*"
                      fullWidth
                      margin="normal"
                    >
                      {states.map((state) => (
                        <MenuItem key={state.code} value={state.code}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Stack>
            </>
          )}

          {/* Submit Button */}
          <Box width="100%" textAlign="center">
            <LoadingButton
              loading={isSendingWotcSurvey}
              sx={{ mt: 3, width: '100%' }}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SnapRecipientFormModal;
